I
<script setup lang="ts">
import ProductHeader from "@/Pages/Products/Partials/ProductHeader.vue";
import ProductMenu from "@/Pages/Products/Partials/ProductMenu.vue";
import TheLayoutApp from "@/Layouts/TheLayoutApp.vue";
import AppContainer from "@/Components/Shared/container/AppContainer.vue";
import AppPage from "@/Components/Shared/page/AppPage.vue";
import AppGrid from "@/Components/Shared/grid/AppGrid.vue";
import AppColumn from "@/Components/Shared/grid/AppColumn.vue";
import AppHeader from "@/Components/Shared/header/AppHeader.vue";
import AppTitle from "@/Components/Shared/title/AppTitle.vue";
import AppArticle from "@/Components/Shared/article/AppArticle.vue";
import AppProductGrid from "@/Components/Shared/product/AppProductGrid.vue";
import AppReviewGrid from "@/Components/App/reviews/AppReviewGrid.vue";
import ProductSpecs from "@/Pages/Products/Partials/ProductSpecs.vue";
import AppLink from "@/Components/Shared/link/AppLink.vue";
import AppCallout from "@/Components/Shared/callout/AppCallout.vue";
import { isLoggedIn } from "@/Utils/helpers";
import AppPostList from "@/Components/App/posts/AppPostList.vue";
import AppPlaceholder from "@/Components/Shared/placeholder/AppPlaceholder.vue";
import AppPostButton from "@/Components/App/posts/AppPostButton.vue";

type Props = {
    product: object;
    latestReviews?: object;
    relatedProducts?: object;
    relatedPosts?: object;
    latestPosts?: object;
    allRatings?: number[];
};

const {
    product,
    latestReviews = undefined,
    relatedProducts = undefined,
    relatedPosts = undefined,
    allRatings = [],
} = defineProps<Props>();

const breadcumbs = [
    {
        label: "Producten",
        path: "/producten",
    },
];

if (product.data.category) {
    if (product.data.category.theme) {
        breadcumbs.push({
            label: product.data.category.theme.name,
            path: product.data.category.theme.path,
        });
    }

    breadcumbs.push({
        label: product.data.category.name,
        path: product.data.category.path,
    });
}
</script>

<template>
    <TheLayoutApp
        :title="product.data.name"
        :description="`Vind alles over ${product.data.name}, met reviews, collecties en tips van anderen, en ontdek de kracht van collectieve aanbevelingen.`"
        :image="product.data.image_path"
        :breadcrumbs="breadcumbs"
    >
        <AppContainer>
            <AppPage size="small">
                <ProductHeader
                    :product="product.data"
                    :all-ratings="allRatings"
                />
                <ProductMenu :product="product.data" />

                <AppGrid>
                    <AppColumn class="md:col-span-7">
                        <div class="flex flex-col gap-8 sm:gap-12">
                            <section v-if="!product.data.not_yet_released">
                                <AppHeader size="small">
                                    <AppTitle
                                        as="h2"
                                        variant="main"
                                    >
                                        Reviews
                                    </AppTitle>
                                </AppHeader>

                                <AppReviewGrid
                                    :reviews="latestReviews.data"
                                    context="product"
                                />

                                <AppLink
                                    v-if="
                                        product.data.reviews_count >
                                        latestReviews.data.length
                                    "
                                    :href="`${product.data.path}/reviews`"
                                    class="mt-4 w-full"
                                    variant="buttonSmall"
                                    icon-after="arrow-right"
                                >
                                    Bekijk alle reviews
                                </AppLink>

                                <template v-if="isLoggedIn">
                                    <AppCallout
                                        v-if="product.data.auth_review"
                                        size="regular"
                                        icon="spark"
                                        title="Update jouw ervaring!"
                                        message="Help anderen door jouw review te updaten. Zo blijft de informatie actueel en relevant."
                                        :link-href="`/toevoegen?product=${product.data.id}`"
                                        link-label="Review updaten"
                                        class="mt-6"
                                    />
                                    <AppCallout
                                        v-else
                                        size="regular"
                                        icon="spark"
                                        title="Deel jouw ervaring!"
                                        message="Help anderen door jouw review te delen en verdien punten voor de community."
                                        :link-href="`/toevoegen?product=${product.data.id}`"
                                        link-label="Review schrijven"
                                        class="mt-6"
                                    />
                                </template>
                            </section>

                            <section v-if="product.data.content">
                                <AppHeader size="small">
                                    <AppTitle
                                        as="h2"
                                        variant="main"
                                    >
                                        Omschrijving
                                    </AppTitle>
                                </AppHeader>

                                <!-- eslint-disable vue/no-v-html -->
                                <AppArticle
                                    size="small"
                                    v-html="product.data.content"
                                />
                            </section>
                        </div>
                    </AppColumn>
                    <AppColumn class="md:col-span-5">
                        <div class="flex flex-col gap-8 sm:gap-12">
                            <ProductSpecs :product="product.data" />

                            <section>
                                <AppHeader>
                                    <AppTitle
                                        as="h3"
                                        variant="subtitle"
                                    >
                                        Recente posts
                                    </AppTitle>
                                </AppHeader>
                                <AppPostList
                                    v-if="
                                        latestPosts && latestPosts.data.length
                                    "
                                    :posts="latestPosts.data"
                                    context="product"
                                />
                                <AppPlaceholder v-else>
                                    Nog geen posts
                                </AppPlaceholder>

                                <AppPostButton
                                    class="mt-4"
                                    :subject="product.data"
                                    button-size="xsmall"
                                    button-color="secondary"
                                />
                            </section>
                        </div>
                    </AppColumn>
                </AppGrid>
            </AppPage>
        </AppContainer>
        <template #sidebar>
            <section v-if="relatedProducts && relatedProducts.data.length">
                <AppHeader>
                    <AppTitle
                        as="h3"
                        variant="subtitle"
                    >
                        Gerelateerde producten
                    </AppTitle>
                </AppHeader>
                <AppProductGrid
                    :products="relatedProducts.data"
                    size="xsmall"
                    card-size="small"
                />
            </section>
        </template>
        <!--        <template-->
        <!--            v-if="latestPosts.data && latestPosts.data.length"-->
        <!--            #sidebar-->
        <!--        >-->
        <!--            <AppHeader-->
        <!--                variant="between"-->
        <!--                gap="small"-->
        <!--            >-->
        <!--                <AppTitle-->
        <!--                    as="h3"-->
        <!--                    variant="subtitle"-->
        <!--                >-->
        <!--                    Posts-->
        <!--                </AppTitle>-->

        <!--                <AppPostButton-->
        <!--                    :subject="product.data"-->
        <!--                    button-size="xsmall"-->
        <!--                    label="Nieuw"-->
        <!--                />-->
        <!--            </AppHeader>-->

        <!--            <AppPostList-->
        <!--                :posts="latestPosts.data"-->
        <!--                context="product"-->
        <!--                class="mb-4"-->
        <!--            />-->
        <!--        </template>-->
        <!--        <template-->
        <!--            v-if="latestPosts.data"-->
        <!--            #sidebarSmall-->
        <!--        >-->
        <!--            <section>-->
        <!--                <AppHeader>-->
        <!--                    <AppTitle-->
        <!--                        as="h3"-->
        <!--                        variant="subtitle"-->
        <!--                        color="gray"-->
        <!--                    >-->
        <!--                        Posts-->
        <!--                    </AppTitle>-->
        <!--                </AppHeader>-->

        <!--                <AppPostList-->
        <!--                    :posts="latestPosts.data"-->
        <!--                    context="product"-->
        <!--                    class="mb-4"-->
        <!--                    hide-placeholder-->
        <!--                />-->

        <!--                <AppText color="gray">-->
        <!--                    Er zijn nog geen posts gemaakt voor dit product. Heb jij een-->
        <!--                    interessante discussie, vraag of tip? Maak een post en deel het-->
        <!--                    met anderen!-->
        <!--                </AppText>-->

        <!--                <AppPostButton-->
        <!--                    class="mt-2"-->
        <!--                    :subject="product.data"-->
        <!--                    button-size="small"-->
        <!--                    label="Nieuwe post"-->
        <!--                />-->
        <!--            </section>-->
        <!--        </template>-->
    </TheLayoutApp>
</template>
